import moment from "moment";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getCheckMoment = (checkTime) => {
  if (checkTime) {
    const parsed = moment(parseInt(checkTime));
    if (parsed.isValid()) return parsed;
  }
  return moment();
};

const getBusinessDay = (checkMoment) => {
  if (checkMoment.hour() < 6) {
    return checkMoment.clone().subtract(1, "day").day();
  }
  return checkMoment.day();
};

const getTimeMoment = (checkMoment, timeValue) => {
  // if (typeof timeValue !== "number") return null;
  const parsed = moment(parseInt(timeValue));
  if (!parsed.isValid()) return null;

  return checkMoment
    .clone()
    .set("hour", parsed.hour())
    .set("minute", parsed.minute())
    .set("second", 0)
    .set("millisecond", 0);
};

const getLimitSlot = (limit, checkMoment) => {
  if (!limit) return null;

  if (Array.isArray(limit.days) && limit.days.length > 0) {
    const businessDay = getBusinessDay(checkMoment);
    return limit.days.find((day) => parseInt(day.day) === businessDay);
  }

  if (limit.startTime && limit.endTime) {
    return {
      startTime: limit.startTime,
      endTime: limit.endTime
    };
  }

  return null;
};

const isIncludeWindow = (limit) => limit && (limit.isInclude === 0 || limit.isInclude === "0");

export const isLimitAvailableAtTime = (limit, checkTime) => {
  if (!limit || !limit.hasLimitTime) return true;

  const checkMoment = getCheckMoment(checkTime);
  const slot = getLimitSlot(limit, checkMoment);
  if (!slot) return true;

  let startTime = getTimeMoment(checkMoment, slot.startTime);
  let endTime = getTimeMoment(checkMoment, slot.endTime);
  if (!startTime || !endTime) return true;

  if (startTime.valueOf() > endTime.valueOf()) {
    if (checkMoment.valueOf() < startTime.valueOf()) {
      startTime = startTime.subtract(1, "day");
    } else {
      endTime = endTime.add(1, "day");
    }
  }

  const isWithin = checkMoment.isSameOrAfter(startTime) && checkMoment.isBefore(endTime);
  return isIncludeWindow(limit) ? isWithin : !isWithin;
};

const getTimeLimits = (item) => {
  if (Array.isArray(item && item.timeLimits) && item.timeLimits.length > 0) {
    return item.timeLimits;
  }
  return item && item.hasLimitTime ? [item] : [];
};

export const isMenuItemAvailableAtTime = (item, checkTime) => {
  const limits = getTimeLimits(item);
  if (limits.length === 0) return true;
  return limits.every((limit) => isLimitAvailableAtTime(limit, checkTime));
};

export const filterMenuListByAvailability = (menuList, checkTime) => {
  if (!Array.isArray(menuList)) return [];

  return menuList
    .filter((menu) => isMenuItemAvailableAtTime(menu, checkTime))
    .map((menu) => {
      const nextMenu = { ...menu };
      if (Array.isArray(nextMenu.type)) {
        nextMenu.type = nextMenu.type
          .filter((item) => isMenuItemAvailableAtTime(item, checkTime))
          .map((item) => {
            if (!item.isCategory || !Array.isArray(item.type)) return item;
            return {
              ...item,
              type: item.type.filter((subItem) => isMenuItemAvailableAtTime(subItem, checkTime))
            };
          })
          .filter((item) => !item.isCategory || !Array.isArray(item.type) || item.type.length > 0);
      }
      return nextMenu;
    })
    .filter((menu) => !Array.isArray(menu.type) || menu.type.length > 0);
};

const formatTime = (timeValue) => {
  if (!timeValue || timeValue === "0") return "";
  const parsed = moment(parseInt(timeValue));
  if (!parsed.isValid()) return "";
  return parsed.format("HH:mm");
};

const formatDays = (days) => {
  if (!Array.isArray(days) || days.length === 0) return "";
  const dayIndexes = [...new Set(days.map((day) => parseInt(day)).filter((day) => day >= 0 && day <= 6))].sort();
  if (dayIndexes.length === 7) return "Everyday";
  return dayIndexes.map((day) => DAY_LABELS[day]).join(", ");
};

const formatLimitSchedule = (limit) => {
  if (!limit || !limit.hasLimitTime) return null;
  const groups = {};

  if (Array.isArray(limit.days) && limit.days.length > 0) {
    limit.days.forEach((day) => {
      const startTime = formatTime(day.startTime);
      const endTime = formatTime(day.endTime);
      if (!startTime || !endTime) return;
      const key = `${startTime}-${endTime}`;
      if (!groups[key]) {
        groups[key] = {
          startTime,
          endTime,
          days: []
        };
      }
      groups[key].days.push(day.day);
    });
  } else {
    const startTime = formatTime(limit.startTime);
    const endTime = formatTime(limit.endTime);
    if (startTime && endTime) {
      groups[`${startTime}-${endTime}`] = {
        startTime,
        endTime,
        days: []
      };
    }
  }

  const schedule = Object.keys(groups)
    .filter((key) => {
      return groups[key].startTime != groups[key].endTime;
    })
    .map((key) => {
      const group = groups[key];
      const dayText = formatDays(group.days);
      return `${dayText ? `${dayText} ` : ""}${group.startTime} - ${group.endTime}`;
    })
    .join("; ");

  if (!schedule) return null;

  const isUnavailable = !isIncludeWindow(limit);
  if (isUnavailable) {
    return null;
  }
  return {
    text: `${isUnavailable ? "Unavailable Time" : "Available Time"}: ${schedule}`,
    isUnavailable
  };
};

export const getMenuAvailabilityInfo = (item) => {
  const limits = getTimeLimits(item);
  const schedules = [];
  const scheduleKeys = {};

  limits.map(formatLimitSchedule).filter(Boolean).forEach((schedule) => {
    const key = `${schedule.isUnavailable}-${schedule.text}`;
    if (scheduleKeys[key]) return;
    scheduleKeys[key] = true;
    schedules.push(schedule);
  });

  if (schedules.length === 0) return null;

  return {
    text: schedules.map((schedule) => schedule.text).join(" | "),
    isUnavailable: schedules.some((schedule) => schedule.isUnavailable)
  };
};

export const getUnavailableCartItems = (cart, checkTime) => {
  if (!checkTime || !Array.isArray(cart)) return [];
  return cart.filter((item) => !isMenuItemAvailableAtTime(item, checkTime));
};
